/* ============================================================
   Dathent — plan display catalog (Phase 4, admin-managed plans)
   Single client-side entry point for DB-driven plan display fields
   (docs/TARIFF-REWORK-BRIEF.md §3/§4). Fetches GET /api/billing/plans
   ONCE per SPA session (module-scope cache, shared across every
   consumer/component instance) and exposes a resolver:

     getPlanDisplay(idOrKey) -> {
       name, description, priceCents, priceWasCents, currency, unit,
       priceFormatted, priceWasFormatted, promoBadge, promoThen,
       limits, features, isSelfService, requiresSalesContact, ctaText,
     }

   CRITICAL SAFETY CONTRACT: this resolver NEVER returns a blank or
   broken price. For every field, if the server response is missing,
   null, or the fetch fails entirely, it falls back to the existing
   hardcoded lib/plans.js PLAN_DEFS value — so the UI renders BYTE-
   IDENTICAL to today until an admin edits a plan's DB row and that
   row's fields actually diverge. lib/plans.js PLAN_DEFS stays the
   fallback source of truth; nothing here deletes or bypasses it.
   ============================================================ */
import { useEffect, useState } from 'react';
import { api } from '../services/api/client.js';
import { PLAN_DEFS, PLAN_KEY_BY_ID, PLAN_ID_BY_KEY, NEW_PLAN_DEFS } from '../lib/plans.js';
import { readCatalogManagedPaymentsFlag } from '../lib/managedPayments.js';

// Module-scope so every component sharing this module shares ONE fetch +
// ONE in-memory cache for the lifetime of the page (no re-fetch per mount).
// The underlying api.billing.getPlans() call also has its own client-level
// GET cache/dedup, but we additionally cache the *parsed* map here so the
// resolver never re-parses or re-fetches on remount.
let _catalogPromise = null;
let _catalogByPlanKey = null; // null = not yet loaded/failed; {} = loaded (possibly empty)
// GH-912 (Stripe Managed Payments): the stripe-worker's
// managedPaymentsNewSubscriptions from the same response. undefined until a
// plans response actually carries the boolean (older backend, fetch pending
// or failed) — consumers then fall back to the entitlements mirror, see
// lib/managedPayments.js resolveManagedPaymentsNewSubscriptions.
let _catalogManagedPaymentsNewSubscriptions;

function parsePlansResponse(res) {
  const list = res?.plans || res?.data?.plans || [];
  const byKey = {};
  for (const p of Array.isArray(list) ? list : []) {
    if (p && typeof p.planKey === 'string') byKey[p.planKey] = p;
  }
  return byKey;
}

// Kicks off (or reuses) the single shared fetch. Resolves to a {} map on ANY
// failure (network error, bad shape, etc.) — callers never need a try/catch;
// an empty map just means every field falls back to PLAN_DEFS.
function loadCatalog() {
  if (!_catalogPromise) {
    _catalogPromise = api.billing
      .getPlans()
      .then((res) => {
        _catalogByPlanKey = parsePlansResponse(res);
        _catalogManagedPaymentsNewSubscriptions = readCatalogManagedPaymentsFlag(res);
        return _catalogByPlanKey;
      })
      .catch(() => {
        _catalogByPlanKey = {};
        return _catalogByPlanKey;
      });
  }
  return _catalogPromise;
}

/**
 * Format integer cents into a display string respecting currency + unit.
 * Only EUR is special-cased with the existing "€150" convention (no space,
 * symbol first) used across the app today; anything else falls back to a
 * simple "<amount> <CUR>" so a future currency never throws.
 */
export function formatPriceCents(cents, currency = 'EUR', unit = '') {
  if (cents === null || cents === undefined || Number.isNaN(Number(cents))) return null;
  const amount = Math.round(Number(cents)) / 100;
  // Whole-euro amounts render without decimals (matches today's "€150"/"€300"
  // hardcoded copy); non-whole amounts keep up to 2 decimals.
  const amountStr = Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
  const cur = (currency || 'EUR').toUpperCase();
  const symbol = cur === 'EUR' ? '€' : cur === 'USD' ? '$' : cur === 'GBP' ? '£' : null;
  const priceText = symbol ? `${symbol}${amountStr}` : `${amountStr} ${cur}`;
  return unit ? `${priceText}${unit}` : priceText;
}

function findPlanDef(idOrKey) {
  if (!idOrKey) return null;
  // Accept either the frontend card id ('pilot') or the backend planKey
  // ('brand_pilot') — resolve both to the same PLAN_DEFS entry.
  const id = PLAN_DEFS.some((p) => p.id === idOrKey) ? idOrKey : PLAN_ID_BY_KEY[idOrKey];
  return PLAN_DEFS.find((p) => p.id === id) || null;
}

/**
 * Pure resolver — safe to call outside React (no hooks). Reads whatever the
 * module-scope cache currently holds (possibly still empty/unloaded); pair
 * with usePlanCatalog() in a component so it re-renders once the fetch lands.
 *
 * @param {string} idOrKey - frontend PLAN_DEFS id ('pilot') or backend planKey ('brand_pilot')
 */
export function getPlanDisplay(idOrKey) {
  const def = findPlanDef(idOrKey);
  // Resolve to the backend planKey either via the matched PLAN_DEFS entry, or
  // (if idOrKey was already a planKey with no PLAN_DEFS entry, e.g. a future
  // DB-only plan) use idOrKey itself.
  const planKey = def ? PLAN_KEY_BY_ID[def.id] : idOrKey;
  const dbPlan = (_catalogByPlanKey && planKey) ? _catalogByPlanKey[planKey] : null;

  // Fallback scaffold from the hardcoded PLAN_DEFS entry. Every DB field is
  // layered on top ONLY when present/non-null — never blanks a field that
  // already has a hardcoded value.
  const fallbackCurrency = 'EUR';
  const fallbackUnit = def?.priceUnit || '/mo';
  const fallbackPriceFormatted = def?.price ?? (def?.soonPrice ?? def?.customPrice ?? null);
  const fallbackPriceWasFormatted = def?.priceWas ?? null;

  const currency = dbPlan?.priceDisplayCurrency || fallbackCurrency;
  const unit = dbPlan?.priceDisplayUnit || fallbackUnit;

  // DISPLAY model (Model B): price_display_cents is the SHOWN price and
  // price_display_was_cents the optional strikethrough. The DB seed holds the
  // ACTUAL displayed numbers (brand_pilot: 15000 shown / 30000 struck — the
  // €150 founding price + €300 list), NOT the Stripe base amount, so we trust
  // the DB price whenever it's set and show a strike only when `was` is set.
  // This is what makes an admin price edit propagate here. A non-discounted
  // tier (price only, no `was`) shows a single price with no fake strike; a
  // null DB price (row absent, field unset, or fetch failed) falls back to the
  // hardcoded PLAN_DEFS string below — never a blank.
  const priceCents = typeof dbPlan?.priceDisplayCents === 'number' ? dbPlan.priceDisplayCents : null;
  const priceWasCents = typeof dbPlan?.priceDisplayWasCents === 'number' ? dbPlan.priceDisplayWasCents : null;

  // priceFormatted is the BARE amount ("€150") with no unit suffix — callers
  // that show unit as its own element (e.g. Plans.jsx "€150" + "/mo" spans)
  // read `unit` separately; combine priceFormatted + unit for an inline string.
  const priceFormatted = priceCents !== null
    ? formatPriceCents(priceCents, currency, '')
    : fallbackPriceFormatted;
  const priceWasFormatted = priceWasCents !== null
    ? formatPriceCents(priceWasCents, currency, '')
    : fallbackPriceWasFormatted;

  return {
    planKey: planKey || null,
    id: def?.id || null,
    name: dbPlan?.displayName || def?.name || null,
    description: dbPlan?.description || def?.desc || null,
    priceCents,
    priceWasCents,
    currency,
    unit,
    priceFormatted,
    priceWasFormatted,
    promoBadge: dbPlan?.promoBadgeText || def?.priceBadge || null,
    promoThen: dbPlan?.promoThenText || def?.priceThen || null,
    // limits/features from the DB row when present, else null (callers that
    // need enforcement-grade limits should use the entitlements endpoint —
    // this is DISPLAY catalog only, per the brief).
    limits: dbPlan?.limits || null,
    features: dbPlan?.features || null,
    isSelfService: typeof dbPlan?.isSelfService === 'boolean' ? dbPlan.isSelfService : !def?.soon,
    requiresSalesContact: typeof dbPlan?.requiresSalesContact === 'boolean'
      ? dbPlan.requiresSalesContact
      : !!def?.contact,
    ctaText: dbPlan?.ctaText || def?.cta || null,
    // True once this specific plan's DB row was actually found (i.e. the
    // fetch succeeded AND the plan exists there) — lets a consumer tell
    // "hardcoded fallback" from "DB row confirmed parity" if it ever cares.
    fromDb: !!dbPlan,
  };
}

/**
 * Same resolver as getPlanDisplay() above, but scoped to the NEW tariff grid
 * (lib/plans.js NEW_PLAN_DEFS — starter/growth/pro/agency, docs/VYTIAG-
 * tarify-core-video-2026-07-06.md §2). Kept as a SEPARATE function rather than
 * folded into getPlanDisplay/findPlanDef: the new grid's 'growth' id shares
 * its string with the legacy PLAN_DEFS 'growth' soon-placeholder, and
 * findPlanDef above resolves against PLAN_DEFS first — reusing it here would
 * silently return the WRONG "Growth" card. This resolver only ever looks at
 * NEW_PLAN_DEFS, so that collision is structurally impossible.
 *
 * For the new grid, idOrKey is always the same string (id === backend
 * planKey), so no id<->key indirection is needed before the DB lookup.
 *
 * @param {string} idOrKey - NEW_PLAN_DEFS id, which is also the backend planKey
 *   ('starter' | 'growth' | 'pro' | 'agency').
 */
export function getNewPlanDisplay(idOrKey) {
  const def = NEW_PLAN_DEFS.find((p) => p.id === idOrKey) || null;
  const planKey = def?.id || idOrKey || null;
  const dbPlan = (_catalogByPlanKey && planKey) ? _catalogByPlanKey[planKey] : null;

  const fallbackCurrency = 'EUR';
  const fallbackUnit = def?.priceUnit || '/mo';
  const fallbackPriceFormatted = def?.price ?? null;
  const fallbackPriceWasFormatted = def?.priceWas ?? null;

  const currency = dbPlan?.priceDisplayCurrency || fallbackCurrency;
  const unit = dbPlan?.priceDisplayUnit || fallbackUnit;

  const priceCents = typeof dbPlan?.priceDisplayCents === 'number' ? dbPlan.priceDisplayCents : null;
  const priceWasCents = typeof dbPlan?.priceDisplayWasCents === 'number' ? dbPlan.priceDisplayWasCents : null;
  const priceFormatted = priceCents !== null
    ? formatPriceCents(priceCents, currency, '')
    : fallbackPriceFormatted;
  const priceWasFormatted = priceWasCents !== null
    ? formatPriceCents(priceWasCents, currency, '')
    : fallbackPriceWasFormatted;

  return {
    planKey,
    id: def?.id || null,
    name: dbPlan?.displayName || def?.name || null,
    description: dbPlan?.description || def?.desc || null,
    priceCents,
    priceWasCents,
    currency,
    unit,
    priceFormatted,
    priceWasFormatted,
    promoBadge: dbPlan?.promoBadgeText || def?.priceBadge || null,
    promoThen: dbPlan?.promoThenText || def?.priceThen || null,
    limits: dbPlan?.limits || null,
    features: dbPlan?.features || null,
    isSelfService: typeof dbPlan?.isSelfService === 'boolean' ? dbPlan.isSelfService : true,
    requiresSalesContact: typeof dbPlan?.requiresSalesContact === 'boolean' ? dbPlan.requiresSalesContact : false,
    ctaText: dbPlan?.ctaText || def?.cta || null,
    fromDb: !!dbPlan,
  };
}

/**
 * The data-driven flip for the NEW tariff grid (docs/VYTIAG-tarify-core-
 * video-2026-07-06.md §2 / docs/TARIFF-REWORK-BRIEF.md §6). The SPA renders
 * ONLY the legacy 4-tier grid until an admin publishes 'starter' AND 'growth'
 * as public, self-service rows in `billing_plans` (Phase 5 day-of-flip
 * checklist) — no SPA deploy required to go live, and nothing here changes
 * what's shown before that day: pre-flip the catalog has neither plan_key, so
 * this stays false and every consumer below takes the untouched legacy branch.
 *
 * Requires BOTH 'starter' and 'growth' (not just one) so a partially-seeded
 * catalog (an admin mid-edit) can't flip the grid on an incomplete set.
 */
export function isNewGridActive() {
  const map = _catalogByPlanKey;
  if (!map) return false;
  const starter = map.starter;
  const growth = map.growth;
  return !!(
    starter && growth
    && starter.isSelfService === true && growth.isSelfService === true
    && !starter.requiresSalesContact && !growth.requiresSalesContact
  );
}

/**
 * Display resolver for the Posting tariff card (TASK-236, #354; PRICING-
 * MODEL-V2.md §2), scoped to the single 'posting' plan_key. Same shape as
 * getNewPlanDisplay — DB row layered over a hardcoded fallback — but its own
 * function (not folded into the other resolvers) because the Posting plan is
 * quantity-billed (price_display_unit '/social/mo'), not a flat tier: callers
 * multiply priceCents by the connected-social count themselves
 * (lib/postingPlan.js's buildPostingPriceLine), which the other resolvers
 * have no reason to do.
 */
export function getPostingPlanDisplay() {
  const planKey = 'posting';
  const dbPlan = _catalogByPlanKey ? _catalogByPlanKey[planKey] : null;

  const currency = dbPlan?.priceDisplayCurrency || 'EUR';
  const unit = dbPlan?.priceDisplayUnit || '/social/mo';
  const priceCents = typeof dbPlan?.priceDisplayCents === 'number' ? dbPlan.priceDisplayCents : null;

  return {
    planKey,
    name: dbPlan?.displayName || 'Scheduler',
    description: dbPlan?.description || 'Schedule and publish your own posts. Billed per connected social account.',
    priceCents,
    currency,
    unit,
    features: dbPlan?.features || null,
    ctaText: dbPlan?.ctaText || 'Start',
    fromDb: !!dbPlan,
  };
}

/**
 * The data-driven flip for the Posting-tariff card (migration
 * 0114_billing_plan_posting.sql: is_public=0/is_self_service=0 until the
 * owner creates the real Stripe product/prices — see that migration's
 * header). GET /api/billing/plans (list-billing-plans.ts) filters
 * onlyPublic=true, so 'posting' is simply ABSENT from the fetched catalog
 * pre-flip: this stays false and the card renders nothing, no SPA deploy
 * needed to go live once the owner flips those columns.
 */
export function isPostingPlanActive() {
  return !!(_catalogByPlanKey && _catalogByPlanKey.posting);
}

/**
 * React hook: triggers the one-time catalog fetch and re-renders the caller
 * once it resolves (success or failure — failure just means "stay on
 * fallback values", never a thrown error or a blank UI).
 *
 * Usage:
 *   const { getPlanDisplay, ready } = usePlanCatalog();
 *   const pilot = getPlanDisplay('pilot');
 */
export function usePlanCatalog() {
  const [ready, setReady] = useState(_catalogByPlanKey !== null);

  useEffect(() => {
    let cancelled = false;
    loadCatalog().then(() => {
      if (!cancelled) setReady(true);
    });
    return () => { cancelled = true; };
  }, []);

  // newGridActive/postingPlanActive are recomputed from the current
  // module-scope cache on every render — cheap (map lookups), and `ready`
  // flipping after the fetch resolves is what triggers the re-render that
  // picks up the real value.
  return {
    getPlanDisplay,
    getNewPlanDisplay,
    getPostingPlanDisplay,
    newGridActive: isNewGridActive(),
    postingPlanActive: isPostingPlanActive(),
    managedPaymentsNewSubscriptions: _catalogManagedPaymentsNewSubscriptions,
    ready,
  };
}

export default usePlanCatalog;
